import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-border disabled:bg-muted disabled:text-muted-foreground [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover",
        secondary:
          "border border-input bg-card text-foreground hover:border-foreground/45 hover:bg-accent",
        outline:
          "border border-input bg-card text-foreground hover:border-foreground/45 hover:bg-accent",
        ghost: "text-foreground hover:bg-accent",
        destructive: "border border-destructive/50 bg-card text-destructive-text hover:bg-destructive/8",
        loading: "bg-primary text-primary-foreground shadow-xs cursor-wait",
        // A selected chip keeps its olive fill on hover. It does not darken.
        chip: "chip-selected bg-primary text-primary-foreground hover:bg-primary",
        lime: "bg-lime text-ink font-bold border border-lime shadow-xs hover:brightness-105 active:brightness-95",
      },
      size: {
        default: "h-11 px-4 md:h-9",
        sm: "h-11 px-3 text-[0.8125rem] md:h-8",
        lg: "h-11 px-5 text-[0.9375rem]",
        icon: "size-11 md:size-9",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ComponentProps<"button">, VariantProps<typeof buttonVariants> {
  /** Render the child element (for example a link) with button styling. */
  asChild?: boolean;
  /** Show a busy indicator and prevent another activation while work is in progress. */
  loading?: boolean;
}

function Button({
  className,
  variant,
  size,
  asChild = false,
  loading,
  disabled,
  children,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot : "button";
  const isLoading = loading ?? variant === "loading";
  if (asChild) {
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled}
        aria-busy={isLoading || undefined}
        {...props}
      >
        {children}
      </Comp>
    );
  }
  return (
    <Comp
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || isLoading}
      aria-busy={isLoading || undefined}
      {...props}
    >
      {isLoading && (
        <span
          className="size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
          aria-hidden
        />
      )}
      {children}
    </Comp>
  );
}

export { Button, buttonVariants };
